import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, X, Film, Maximize, Minimize } from 'lucide-react';
import { getProject } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDuration } from '../lib/utils';

const DEFAULT_DURATION = 3;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getShotImageSrc(shot) {
  if (shot.frame_path_temp) return shot.frame_path_temp;
  if (shot.frame_path) return API_URL + shot.frame_path;
  return null;
}

function EditableDuration({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(value.toFixed(1));
    setEditing(true);
  };

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const n = parseFloat(draft);
    if (!isNaN(n) && n > 0) onChange(n);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full h-7 bg-white/10 border border-white/20 text-white/90 text-sm text-center rounded-md px-2 outline-none focus:ring-1 focus:ring-primary focus:border-primary tabular-nums"
        data-testid="duration-input"
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      className="w-full h-7 text-sm text-white/50 hover:text-white/80 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-md tabular-nums transition-all cursor-text flex items-center justify-center"
      data-testid="duration-display"
    >
      {value.toFixed(1)}s
    </button>
  );
}

export default function SlideshowView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [shots, setShots] = useState([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimer = useRef(null);
  const thumbStripRef = useRef(null);

  // Preload all shot images into browser cache
  useEffect(() => {
    shots.forEach(s => {
      const src = getShotImageSrc(s);
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });
  }, [shots]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getProject(id);
        setShots(data.shots || []);
        setTitle(data.title || '');
      } catch {
        navigate('/', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const totalShots = shots.length;
  const currentShot = shots[current] || null;

  const getDuration = useCallback((shot) => {
    const d = parseFloat(shot.duration);
    return d > 0 ? d : DEFAULT_DURATION;
  }, []);

  const currentDuration = currentShot ? getDuration(currentShot) : DEFAULT_DURATION;

  // Playback timer — 50ms ticks
  useEffect(() => {
    if (!playing || totalShots === 0) return;

    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.05;
        if (next >= currentDuration) {
          setCurrent(c => {
            if (c + 1 >= totalShots) {
              setPlaying(false);
              return c;
            }
            return c + 1;
          });
          return 0;
        }
        return next;
      });
    }, 50);

    return () => clearInterval(timerRef.current);
  }, [playing, current, currentDuration, totalShots]);

  // Scroll thumbnail strip to keep current visible
  useEffect(() => {
    if (!thumbStripRef.current) return;
    const thumb = thumbStripRef.current.children[current];
    if (thumb) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [current]);

  const goTo = useCallback((idx) => {
    if (idx < 0 || idx >= totalShots) return;
    setCurrent(idx);
    setElapsed(0);
    // If playing, playback continues from the beginning of the new shot automatically
  }, [totalShots]);

  const togglePlay = useCallback(() => {
    if (!playing && current >= totalShots - 1 && elapsed >= currentDuration) {
      setCurrent(0);
      setElapsed(0);
      setPlaying(true);
    } else {
      setPlaying(p => !p);
    }
  }, [playing, current, totalShots, elapsed, currentDuration]);

  const goNext = useCallback(() => {
    if (current < totalShots - 1) goTo(current + 1);
  }, [current, totalShots, goTo]);

  const goPrev = useCallback(() => {
    if (elapsed > 0.5) {
      setElapsed(0);
    } else if (current > 0) {
      goTo(current - 1);
    }
  }, [current, elapsed, goTo]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handler = (e) => {
      // Don't intercept if editing a duration input
      if (e.target.tagName === 'INPUT') return;
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'ArrowRight' || e.key === 'l') goNext();
      else if (e.key === 'ArrowLeft' || e.key === 'j') goPrev();
      else if (e.key === 'Escape') {
        if (document.fullscreenElement) document.exitFullscreen();
        else navigate(`/project/${id}`);
      }
      else if (e.key === 'f') toggleFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePlay, goNext, goPrev, navigate, id, toggleFullscreen]);

  // Auto-hide controls
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideControlsTimer.current);
    if (playing) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [playing]);

  useEffect(() => {
    if (playing) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    } else {
      setShowControls(true);
      clearTimeout(hideControlsTimer.current);
    }
    return () => clearTimeout(hideControlsTimer.current);
  }, [playing]);

  const handleDurationChange = useCallback((idx, newDuration) => {
    setShots(prev => prev.map((s, i) => i === idx ? { ...s, duration: newDuration } : s));
  }, []);

  // Global progress
  const totalTime = shots.reduce((a, s) => a + getDuration(s), 0);
  const elapsedBefore = shots.slice(0, current).reduce((a, s) => a + getDuration(s), 0);
  const globalProgress = totalTime > 0 ? ((elapsedBefore + elapsed) / totalTime) * 100 : 0;

  // Progress bar seek
  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetTime = pct * totalTime;
    let acc = 0;
    for (let i = 0; i < shots.length; i++) {
      const d = getDuration(shots[i]);
      if (acc + d > targetTime) {
        setCurrent(i);
        setElapsed(targetTime - acc);
        return;
      }
      acc += d;
    }
    // Edge: clicked at the very end
    if (shots.length > 0) {
      setCurrent(shots.length - 1);
      setElapsed(getDuration(shots[shots.length - 1]));
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[200]">
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (totalShots === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-[200] text-white/60">
        <Film className="w-12 h-12 mb-4 opacity-30" />
        <p className="text-sm">Aucun plan dans ce projet</p>
        <button onClick={() => navigate(`/project/${id}`)} className="mt-6 text-xs text-white/40 hover:text-white underline transition-colors">
          Retour au projet
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black flex flex-col z-[200] select-none"
      onMouseMove={resetControlsTimer}
      onClick={resetControlsTimer}
      data-testid="slideshow-container"
    >
      {/* Top bar — title + close */}
      <div className={`absolute top-0 inset-x-0 z-30 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center">
            <span className="text-white/80 font-bold text-xs">K</span>
          </div>
          <span className="text-white/70 text-sm font-medium truncate max-w-[300px]">{title}</span>
          <span className="text-white/30 text-xs">#{current + 1}</span>
        </div>
        <button
          onClick={() => navigate(`/project/${id}`)}
          className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-40"
          data-testid="slideshow-close-btn"
        >
          <X className="w-4 h-4 text-white/80" />
        </button>
      </div>

      {/* Main image — all images stacked, only current visible (no remount = no flash) */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center p-10 pb-52 pt-16">
        {shots.map((shot, i) => {
          const src = getShotImageSrc(shot);
          const isActive = i === current;
          if (src) {
            return (
              <img
                key={shot.id}
                src={src}
                alt={`Plan ${i + 1}`}
                className="absolute max-w-[85%] max-h-full object-contain rounded-lg transition-opacity duration-150"
                style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}
                draggable={false}
                data-testid={isActive ? "slideshow-image" : undefined}
              />
            );
          }
          if (isActive) {
            return (
              <div key={shot.id} className="flex flex-col items-center justify-center text-white/20" data-testid="slideshow-empty-frame">
                <Film className="w-16 h-16 mb-3" />
                <span className="text-sm">Pas de vignette</span>
              </div>
            );
          }
          return null;
        })}

        {/* Shot description overlay */}
        {currentShot.description && (
          <div className={`absolute bottom-52 left-10 right-10 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-white/50 text-sm leading-relaxed line-clamp-2 max-w-2xl" data-testid="slideshow-description">
              {currentShot.description}
            </p>
          </div>
        )}
      </div>

      {/* Bottom panel — thumbnails + progress + controls */}
      <div className={`absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/95 to-transparent pt-8 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>

        {/* Thumbnail strip — larger with editable timing */}
        <div className="px-4 mb-3">
          <div ref={thumbStripRef} className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {shots.map((s, i) => {
              const src = getShotImageSrc(s);
              const isActive = i === current;
              const dur = getDuration(s);
              return (
                <div key={s.id} className="shrink-0 flex flex-col items-center gap-1" style={{ width: 150 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); goTo(i); }}
                    className={`w-full rounded-md overflow-hidden border-2 transition-all duration-200 ${isActive ? 'border-primary shadow-lg shadow-primary/25' : 'border-white/10 opacity-60 hover:opacity-90 hover:border-white/25'}`}
                    style={{ aspectRatio: '16/9' }}
                    data-testid={`slideshow-thumb-${i}`}
                  >
                    {src ? (
                      <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                    ) : (
                      <div className="w-full h-full bg-white/5 flex items-center justify-center">
                        <span className="text-xs text-white/20">{i + 1}</span>
                      </div>
                    )}
                  </button>
                  <EditableDuration
                    value={dur}
                    onChange={(v) => handleDurationChange(i, v)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress bar — thicker, orange */}
        <div
          className="h-2 bg-white/10 cursor-pointer relative mx-4 rounded-full overflow-hidden group"
          onClick={handleProgressClick}
          data-testid="slideshow-progress-bar"
        >
          <div
            className="h-full rounded-full transition-[width] duration-75 ease-linear"
            style={{ width: `${globalProgress}%`, backgroundColor: '#f97316' }}
          />
          {/* Playhead dot */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md shadow-black/50 transition-[left] duration-75 ease-linear pointer-events-none"
            style={{ left: `calc(${globalProgress}% - 6px)` }}
          />
          {/* Shot dividers */}
          {shots.map((s, i) => {
            if (i === 0) return null;
            const pos = shots.slice(0, i).reduce((a, sh) => a + getDuration(sh), 0) / totalTime * 100;
            return <div key={i} className="absolute top-0 bottom-0 w-px bg-white/15" style={{ left: `${pos}%` }} />;
          })}
        </div>

        {/* Controls — centered */}
        <div className="flex items-center justify-center gap-4 py-3 px-4">
          <span className="text-white/30 text-xs tabular-nums w-24 text-right">
            {formatDuration(elapsedBefore + elapsed)}
          </span>

          <div className="flex items-center gap-1.5">
            <button
              onClick={goPrev}
              className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="slideshow-prev-btn"
            >
              <SkipBack className="w-4 h-4 text-white/80" />
            </button>
            <button
              onClick={togglePlay}
              className="w-11 h-11 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
              data-testid="slideshow-play-btn"
            >
              {playing
                ? <Pause className="w-5 h-5 text-white" />
                : <Play className="w-5 h-5 text-white ml-0.5" />
              }
            </button>
            <button
              onClick={goNext}
              className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="slideshow-next-btn"
            >
              <SkipForward className="w-4 h-4 text-white/80" />
            </button>
          </div>

          <span className="text-white/30 text-xs tabular-nums w-24">
            {formatDuration(totalTime)}
          </span>

          <div className="absolute right-4 flex items-center gap-3">
            <span className="text-white/40 text-xs tabular-nums" data-testid="slideshow-counter">
              {current + 1} / {totalShots}
            </span>
            <button
              onClick={toggleFullscreen}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="slideshow-fullscreen-btn"
            >
              {isFullscreen
                ? <Minimize className="w-3.5 h-3.5 text-white/70" />
                : <Maximize className="w-3.5 h-3.5 text-white/70" />
              }
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
